import React, { useMemo, useRef } from 'react';
import {
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
} from '@material-ui/core';

import {
  DataGridPro as DataGrid,
  GridColDef,
  GridSelectionModel,
  GridAlignment,
} from '@mui/x-data-grid-pro';
import clsx from 'clsx';
import { AppColors } from '../theme';
import { useIsPrinting } from './print-context';
import { ClientDeviceType, useDeviceType } from '../utils';
import { LoadingIndicator } from './loading-indicator';

export type IDataTableColumn<T> = {
  valueFactory?: (item: T) => React.ReactNode;
  headerClass?: string;
  itemClass?: string;
  sortable?: boolean;
  filterable?: boolean;
  colSpan?: number;
  align?: GridAlignment;
  hidden?: boolean;
  printable?: boolean;
  mobileHidden?: true;
} & ColumnTitle;

type ColumnTitle = { title: string; name?: string } | { title: React.ReactNode; name: string };

export interface IDataTableNonValueColumn {
  value?: React.ReactNode;
  className?: string;
  colSpan?: number;
  align?: GridAlignment;
}

export interface DataTableProps<T> {
  items: T[];
  columns: IDataTableColumn<T>[];
  getItemKey: (item: T) => number | string;
  stickyHeader?: boolean;
  tableClass?: string;
  rowClass?: (item: T) => string;
  headerClass?: string;
  footerClass?: string;
  additionalHeaderColumns?: IDataTableNonValueColumn[];
  zebra?: boolean;
  onRowClick?: (item: T) => void;
  checkboxSelection?: boolean;
  onSelectionModelChange?: (model: GridSelectionModel) => void;
  pagination?: boolean;
  pageSize?: number;
  selectionModel?: any;
  loading?: boolean;
  isRowSelectable?: (params: T, details?: any) => boolean;
}

interface StylesProps {
  isMobile: boolean;
  colNum: number;
}

const useStyles = makeStyles<Theme, StylesProps>(() => {
  return {
    root: {
      // The following styles are required to allow the data grid cell contents to wrap and have grid rows grow in height
      // this essentially completely disables the grid's automatic sizing behavior until the following github issue is resolved:
      // https://github.com/mui-org/material-ui-x/issues/417
      '& .MuiDataGrid-window, & .MuiDataGrid-columnsContainer': {
        position: 'relative',
        top: '0 !important',
      },
      '& .MuiDataGrid-windowContainer, & .MuiDataGrid-viewport, & .MuiDataGrid-renderingZone, & .MuiDataGrid-row, & .MuiDataGrid-cell': {
        height: 'unset !important',
        maxHeight: 'fit-content !important',
      },
      '& .MuiDataGrid-viewport': {
        minHeight: 56, // an empty grid still needs 1 "row" of height
      },
      '& .MuiDataGrid-dataContainer': {
        minHeight: 'unset !important',
      },
      '& .MuiDataGrid-cell': {
        flexWrap: 'wrap',
        lineHeight: '1rem !important',
        whiteSpace: 'normal',
        paddingTop: 8,
        paddingBottom: 8,
        display: 'grid',
        '& p, & span': {
          cursor: 'text',
        },
        '& button p, & button span': {
          cursor: 'pointer',
        },
      },
      '& .MuiDataGrid-columnsContainer': {
        lineHeight: '1rem !important',
      },
      '& .MuiDataGrid-columnHeaderWrapper': {
        height: 56,
      },
      '& .MuiDataGrid-columnHeaderTitleContainer': {
        whiteSpace: 'normal',
      },
    },
    table: ({ isMobile, colNum }) => ({
      minWidth: isMobile ? `${colNum * 12}em` : 'unset',
      minHeight: '9em',
    }),
    zebra: {
      '& .MuiDataGrid-row:nth-child(odd)': {
        backgroundColor: AppColors.LightGray,
      },
    },
    clickableRows: {
      '& .MuiDataGrid-row, & .MuiDataGrid-cell': {
        cursor: 'pointer',
      },
      '& .MuiDataGrid-cell p, & .MuiDataGrid-cell span': {
        cursor: 'text',
      },
      '& .MuiDataGrid-cell button, & .MuiDataGrid-cell button p, & .MuiDataGrid-cell button span': {
        cursor: 'pointer',
      },
    },
    loadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      opacity: 0.7,
      backgroundColor: AppColors.White,
      zIndex: 1,
    },
  };
});

export function DataTable<T = any>(props: React.PropsWithChildren<DataTableProps<T>>) {
  const {
    items,
    columns,
    getItemKey,
    rowClass,
    checkboxSelection,
    onSelectionModelChange,
    zebra = true,
    children,
    onRowClick,
    loading,
    isRowSelectable,
  } = props;

  const deviceType = useDeviceType();

  const colNum = useMemo(() => columns.length, [columns]);
  const styles = useStyles({ isMobile: deviceType !== ClientDeviceType.Desktop, colNum });
  const gridCols: GridColDef[] = useMemo(
    () =>
      columns.map((x, i) => {
        const show = deviceType === ClientDeviceType.Desktop || !x.mobileHidden;
        let gridCol: GridColDef = {
          field: typeof x.title === 'string' ? x.title : x.name!,
          disableColumnMenu: true,
          sortable: x.sortable,
          filterable: x.filterable,
          headerAlign: x.align,
          headerClassName: x.headerClass,
          renderHeader: () => x.title,
          cellClassName: x.itemClass,
          flex: x.colSpan ?? 1,
          align: x.align,
          hide: !show || x.hidden,
          renderCell: !!x.valueFactory ? params => x.valueFactory?.(params.row as T) : undefined,
        };
        return gridCol;
      }),
    [columns, deviceType]
  );

  const isPrinting = useIsPrinting();
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null);

  const CLICK_DRAG_THRESHOLD_PX = 4;

  const recordPointerDown = (event: React.MouseEvent<HTMLDivElement>) => {
    pointerDownRef.current = { x: event.clientX, y: event.clientY };
  };

  const isDragSelectionGesture = (event?: { clientX?: number; clientY?: number }) => {
    if (!pointerDownRef.current || event?.clientX == null || event?.clientY == null) {
      return false;
    }

    const dx = event.clientX - pointerDownRef.current.x;
    const dy = event.clientY - pointerDownRef.current.y;
    return Math.hypot(dx, dy) > CLICK_DRAG_THRESHOLD_PX;
  };

  const shouldSuppressRowClick = (
    eventCurrentTarget?: EventTarget | null,
    hasDragGesture?: boolean
  ) => {
    if (typeof window === 'undefined') {
      return false;
    }

    const selection = window.getSelection?.();
    if (!selection || selection.isCollapsed || !selection.toString().trim()) {
      return false;
    }

    if (!hasDragGesture) {
      return false;
    }

    // Only suppress when the active selection is within the clicked row.
    if (!eventCurrentTarget || !(eventCurrentTarget instanceof Node)) {
      return false;
    }

    return (
      (!!selection.anchorNode && eventCurrentTarget.contains(selection.anchorNode)) ||
      (!!selection.focusNode && eventCurrentTarget.contains(selection.focusNode))
    );
  };

  const handleRowClick = onRowClick
    ? (x: any, event: any) => {
        const dragGesture = isDragSelectionGesture(event?.nativeEvent ?? event);
        if (shouldSuppressRowClick(event?.currentTarget, dragGesture)) {
          pointerDownRef.current = null;
          return;
        }

        onRowClick(x.row as T);
        pointerDownRef.current = null;
      }
    : undefined;

  if (isPrinting) return <PrintableDataTable {...props} />;

  return (
    //Boxes prevent table from infinitely growing horizontally.
    //See https://github.com/mui/mui-x/issues/8895#issuecomment-1793433389
    <div style={{ display: 'grid', gridTemplateRows: '1fr', minHeight: 0 }}>
      <div style={{ overflow: 'auto' }} onMouseDown={recordPointerDown}>
        <DataGrid
          ref={attachCopyBehavior}
          autoHeight
          hideFooterPagination={!props.pagination}
          disableColumnResize
          disableColumnReorder
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableMultipleColumnsSorting
          disableMultipleColumnsFiltering
          disableDensitySelector
          disableVirtualization
          selectionModel={props.selectionModel}
          className={clsx(
            styles.root,
            zebra && styles.zebra,
            onRowClick && styles.clickableRows,
            styles.table,
            props.tableClass
          )}
          columns={gridCols}
          columnBuffer={0}
          disableExtendRowFullWidth={true}
          density="compact"
          components={{
            Footer: !props.pagination ? () => <>{children}</> : undefined,
            LoadingOverlay: () => (
              <Grid
                className={styles.loadingOverlay}
                container
                alignItems="center"
                justifyContent="center"
              >
                <LoadingIndicator />
              </Grid>
            ),
          }}
          checkboxSelection={checkboxSelection}
          isRowSelectable={isRowSelectable && (x => isRowSelectable(x.row as T))}
          rows={items}
          onRowClick={handleRowClick}
          getRowClassName={rowClass && (x => rowClass(x.row as T))}
          onSelectionModelChange={onSelectionModelChange}
          getRowId={row => getItemKey(row as T)}
          pageSize={props.pageSize}
          pagination={props.pagination}
          loading={loading ?? undefined}
        />
      </div>
    </div>
  );
}

function PrintableDataTable<T>(props: DataTableProps<T>) {
  const cols = props.columns.filter(col => !col.hidden && col.printable !== false);
  return (
    <TableContainer className={props.tableClass}>
      <Table size="small">
        <TableHead>
          <TableRow className={props.headerClass}>
            {cols.map((col, i) => (
              <TableCell key={i} align={col.align}>
                {col.title}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {props.items.map((item, r) => (
            <TableRow key={r} className={props.rowClass?.(item)}>
              {cols.map((col, c) => (
                <TableCell key={c} align={col.align}>
                  {col.valueFactory?.(item)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function attachCopyBehavior(node: HTMLDivElement | null) {
  const listener = (e: ClipboardEvent) => {
    const selection = window.getSelection();
    const range = selection?.getRangeAt(0);
    const doc = range?.cloneContents();

    if (!doc || !e.clipboardData) return;

    // get the selected rows
    const rows: Array<ParentNode> = Array.from(
      doc.querySelectorAll('.MuiDataGrid-row,.MuiDataGrid-columnsContainer')
    );

    // for a single row, treat the selected range as the row
    if (!rows.length) rows.push(doc);

    // transform mui row/cell divs to tab delimited data
    const tab = rows
      .map(row =>
        Array.from(row.querySelectorAll('.MuiDataGrid-cell,.MuiDataGrid-columnHeader'))
          .map(col => col.textContent)
          .join('\t')
      )
      .join('\n');

    if (tab) {
      // if we have rows selected, use the transformed data
      e.clipboardData.setData('text/plain', tab);
      // prevent the default copy-paste behavior
      e.preventDefault();
    }

    // otherwise, the default copy-paste behavior will proceed
  };

  if (node) {
    node.addEventListener('copy', listener);
  }
}
